import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useSite } from "../context/SiteContext";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/motion";
import SEO from "../components/SEO";
import { IMAGES } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-ink/20 bg-cream px-4 py-3.5 text-sm text-ink placeholder:text-stone2/50 focus:border-rouge focus:outline-none transition-colors duration-300";

const BookingForm = () => {
  const { t } = useLanguage();
  const f = t.services.form;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [avail, setAvail] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);

  useEffect(() => {
    if (!form.date) { setAvail(null); return; }
    setAvailLoading(true);
    setForm((prev) => ({ ...prev, time: "" }));
    axios.get(`${API}/availability`, { params: { date: form.date } })
      .then((r) => setAvail(r.data))
      .catch(() => setAvail(null))
      .finally(() => setAvailLoading(false));
  }, [form.date]);

  const refreshAvail = () => {
    if (!form.date) return;
    axios.get(`${API}/availability`, { params: { date: form.date } }).then((r) => setAvail(r.data)).catch(() => {});
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: booking } = await axios.post(`${API}/bookings`, form);
      sessionStorage.setItem("oin-last-booking", JSON.stringify(booking));
      try {
        const { data: co } = await axios.post(`${API}/payments/deposit`, { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      } catch {
        setResult(booking);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(t.slots.conflict);
        refreshAvail();
      } else {
        toast.error(t.services.error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const serviceName = t.services.list.find((s) => s.id === result.service)?.name || result.service;
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="border border-ink/15 bg-sand p-10 text-ink lg:p-14"
        data-testid="booking-success"
      >
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-rouge text-cream">
          <Check size={24} />
        </span>
        <h3 className="mt-8 font-display text-3xl font-bold lg:text-4xl">{t.services.successTitle}</h3>
        <p className="mt-5 max-w-lg text-sm leading-relaxed text-stone2">
          {t.services.successText
            .replace("{name}", result.name)
            .replace("{service}", serviceName)
            .replace("{date}", result.date)
            .replace("{time}", result.time)}
        </p>
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-stone2">
          {t.services.successRef}: <span className="font-bold text-ink">{result.id.slice(0, 8)}</span>
        </p>
        <button
          onClick={() => { setResult(null); setForm({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" }); }}
          data-testid="booking-again-button"
          className="mt-10 border border-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream"
        >
          {t.services.successAgain}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="booking-form">
      <div>
        <label htmlFor="bk-name" className="overline-label mb-2.5 block text-stone2">{f.name}</label>
        <input id="bk-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" autoComplete="name" />
      </div>
      <div>
        <label htmlFor="bk-email" className="overline-label mb-2.5 block text-stone2">{f.email}</label>
        <input id="bk-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" autoComplete="email" />
      </div>
      <div>
        <label htmlFor="bk-phone" className="overline-label mb-2.5 block text-stone2">{f.phone}</label>
        <input id="bk-phone" type="tel" required minLength={6} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" autoComplete="tel" />
      </div>
      <div>
        <label htmlFor="bk-service" className="overline-label mb-2.5 block text-stone2">{f.service}</label>
        <select id="bk-service" required value={form.service} onChange={set("service")} className={inputCls} data-testid="booking-service-select">
          <option value="" disabled>{f.servicePlaceholder}</option>
          {t.services.list.map((s) => (
            <option key={s.id} value={s.id}>{`${s.name} — ${s.from ? t.services.from + " " : ""}${s.price}`}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="bk-date" className="overline-label mb-2.5 block text-stone2">{f.date}</label>
        <input id="bk-date" type="date" required min={new Date().toISOString().split("T")[0]} value={form.date} onChange={set("date")} className={inputCls} data-testid="booking-date-input" />
      </div>
      <div className="sm:col-span-2">
        <p className="overline-label mb-2.5 text-stone2">{f.time}</p>
        {!form.date ? (
          <p className="border border-dashed border-cream/25 px-4 py-3.5 text-sm text-cream/50" data-testid="slot-hint">{t.slots.pickDate}</p>
        ) : availLoading ? (
          <p className="text-sm text-cream/50" data-testid="slot-loading">{t.slots.loading}</p>
        ) : avail?.closed ? (
          <p className="border border-dashed border-cream/25 px-4 py-3.5 text-sm text-cream/50" data-testid="slot-closed">{t.slots.closedSunday}</p>
        ) : avail ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-7" data-testid="slot-picker" role="radiogroup" aria-label={f.time}>
            {avail.slots.map((time) => {
              const taken = avail.booked.includes(time);
              const selected = form.time === time;
              return (
                <button
                  key={time}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={taken}
                  onClick={() => setForm({ ...form, time })}
                  data-testid={`slot-${time.replace(":", "")}`}
                  className={`border px-2 py-3 text-sm font-semibold transition-colors duration-300 ${
                    taken
                      ? "cursor-not-allowed border-cream/10 text-cream/25 line-through"
                      : selected
                        ? "border-rouge bg-rouge text-cream"
                        : "border-cream/25 text-cream hover:border-blush"
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="bk-notes" className="overline-label mb-2.5 block text-stone2">{f.notes}</label>
        <textarea id="bk-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder={f.notesPlaceholder} className={`${inputCls} resize-none`} data-testid="booking-notes-input" />
      </div>
      <div className="sm:col-span-2">
        <p className="text-xs leading-relaxed text-cream/50" data-testid="deposit-note">{t.payment.depositNote}</p>
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={loading || !form.time}
          data-testid="booking-submit-button"
          className="group inline-flex w-full items-center justify-center gap-3 bg-rouge px-8 py-4.5 py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60 sm:w-auto"
        >
          {loading ? f.sending : t.payment.submitPay}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
};

const Services = () => {
  const { t } = useLanguage();
  const { img } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("betaling") === "geannuleerd") {
      toast(t.payment.cancelToast);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);
  return (
    <>
      <SEO page="services" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 lg:px-12 lg:pt-48" data-testid="services-header">
        <p className="overline-label text-rouge">{t.services.overline}</p>
        <h1 className="mt-8 font-display text-5xl font-bold leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl">
          <MaskedLines
            inView
            lines={[
              <span>{t.services.title}</span>,
              <span className="italic font-medium text-rouge">{t.services.titleItalic}</span>,
            ]}
          />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-stone2 md:text-lg">{t.services.sub}</p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12" data-testid="services-list">
        <div className="grid gap-px border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-3">
          {t.services.list.map((s, i) => (
            <FadeUp key={s.id} delay={(i % 3) * 0.1} className="group flex flex-col bg-cream p-10 transition-colors duration-500 hover:bg-sand" data-testid={`service-card-${s.id}`}>
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="font-display text-2xl font-bold leading-tight">{s.name}</h2>
                <p className="shrink-0 font-display text-xl font-bold text-rouge">
                  {s.from && <span className="mr-1 text-xs font-sans font-medium text-stone2">{t.services.from}</span>}
                  {s.price}
                </p>
              </div>
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone2">
                <Clock size={12} /> ± {s.duration} {t.services.duration}
              </p>
              <p className="mt-5 flex-1 text-sm leading-relaxed text-stone2">{s.desc}</p>
              <a href="#boeken" className="link-underline mt-8 w-fit text-xs font-bold uppercase tracking-[0.2em] text-ink group-hover:text-rouge transition-colors duration-300">
                {t.nav.book}
              </a>
            </FadeUp>
          ))}
        </div>
        <p className="mt-6 text-xs text-stone2">{t.services.priceNote}</p>
      </section>

      <section id="boeken" className="bg-ink py-28 text-cream lg:py-36" data-testid="booking-section">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="overline-label text-blush">{t.nav.book}</p>
              <h2 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t.services.bookingTitle}</h2>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-cream/60">{t.services.bookingSub}</p>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-12 hidden lg:block">
              <ParallaxImage
                src={img("booking", IMAGES.booking)}
                alt="Nagelstudio On It Nails Herzele — manicure op maat"
                className="aspect-[4/5] max-w-sm"
                speed={0.1}
              />
            </FadeUp>
          </div>
          <FadeUp delay={0.1} className="lg:col-span-7 [&_input]:bg-transparent [&_input]:text-cream [&_input]:border-cream/25 [&_select]:bg-transparent [&_select]:text-cream [&_select]:border-cream/25 [&_select>option]:text-ink [&_textarea]:bg-transparent [&_textarea]:text-cream [&_textarea]:border-cream/25 [&_label]:text-cream/50">
            <BookingForm />
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Services;
